import React, { useState, useMemo } from 'react'
import {
  Grid,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@the-platform-group/component-library'
import {
  ExpandMore as ExpandMoreIcon,
  MoreVert as MoreVertIcon,
} from '@the-platform-group/component-library/Icons'
import { usePeopleMergeContext } from 'pages/MergeProfiles/contexts/PeopleMergeContext'
import {
  getJobTitleAndOrgName,
  getJobFieldValue,
  getJobOverrideValue,
} from 'pages/MergeProfiles/utils'
import { JOB_CORE_FIELDS } from 'pages/MergeProfiles/constants'
import { StyledCell } from '../../ComparisonRow'

/**
 * MappedJobAccordion - Expandable accordion showing mapped job details in the final review
 * Displays each mapped FactSet job in an accordion format within the "Preview & Merge" section.
 * @param {Object} props
 * @param {Object} props.targetJob - The FactSet job to display
 * @param {Function} props.onEdit - Callback to edit the job mapping (opens JobMapDrawer)
 * @param {Function} props.onViewDetails - Callback to view full details (opens JobDetailsDrawer)
 *
 * @returns {React.Component} Accordion component with job details
 */
const MappedJobAccordion = ({ targetJob, onEdit, onViewDetails }) => {
  const { mergeData, jobMappingData } = usePeopleMergeContext()
  const { sourceJobs } = mergeData.selectedData
  const { getMappedSourceJobsForTarget } = jobMappingData

  const [anchorEl, setAnchorEl] = useState(null)

  const mappedSourceJobs = useMemo(() => {
    return getMappedSourceJobsForTarget(targetJob.id, sourceJobs)
  }, [targetJob.id, getMappedSourceJobsForTarget, sourceJobs])

  const handleMenuOpen = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = event => {
    if (event) {
      event.stopPropagation()
    }
    setAnchorEl(null)
  }

  const handleEdit = event => {
    event.stopPropagation()
    setAnchorEl(null)
    if (mappedSourceJobs.length > 0) {
      const sourceJobIds = mappedSourceJobs.map(job => job.id)
      onEdit(sourceJobIds, targetJob.id)
    }
  }

  const handleViewDetails = event => {
    event.stopPropagation()
    setAnchorEl(null)
    onViewDetails(targetJob.id)
  }

  return (
    <Accordion
      sx={{
        '&:before': { display: 'none' },
        boxShadow: 'none',
        borderBottom: '1px solid',
        borderColor: 'border.secondary',
        width: '100%',
      }}
      disableGutters
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          flexDirection: 'row-reverse',
          minHeight: 48,
          '&.Mui-expanded': {
            minHeight: 48,
          },
          '& .MuiAccordionSummary-content': {
            margin: 0,
            '&.Mui-expanded': {
              margin: 0,
            },
          },
          px: 2,
        }}
      >
        <Grid container alignItems="center" spacing={0}>
          <Grid item xs={10}>
            <Typography variant="body2" noWrap>
              {getJobTitleAndOrgName(targetJob)}
            </Typography>
          </Grid>
          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <IconButton size="small" onClick={handleMenuOpen} sx={{ p: 0.5 }}>
              <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
              <MenuItem onClick={handleEdit}>Edit Job</MenuItem>
              <MenuItem onClick={handleViewDetails}>View job details</MenuItem>
            </Menu>
          </Grid>
        </Grid>
      </AccordionSummary>

      <AccordionDetails sx={{ p: 0, width: '100%' }}>
        <Grid container direction="column" sx={{ width: '100%' }}>
          {JOB_CORE_FIELDS.map(field => {
            const factSetValue = getJobFieldValue(targetJob, field.key)
            const override = getJobOverrideValue(mappedSourceJobs, field.key)
            const hasOverride = !!override

            return (
              <Grid
                key={field.key}
                item
                container
                sx={{
                  borderBottom: '1px solid',
                  borderColor: 'border.secondary',
                  '&:last-child': {
                    borderBottom: 'none',
                  },
                }}
              >
                <StyledCell filled item xs={2} sx={{ p: 1.5 }}>
                  <Typography variant="body2">{field.label}</Typography>
                </StyledCell>
                <StyledCell filled item xs={10} sx={{ p: 1.5 }}>
                  {hasOverride ? (
                    <Grid container spacing={2}>
                      <Grid item>
                        <Typography variant="body2" sx={{ color: 'text.success.default' }}>
                          {override.value} (Platform)
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.critical.default',
                            textDecoration: 'line-through',
                          }}
                        >
                          {factSetValue} (FactSet)
                        </Typography>
                      </Grid>
                    </Grid>
                  ) : (
                    <Typography variant="body2">{factSetValue}</Typography>
                  )}
                </StyledCell>
              </Grid>
            )
          })}
        </Grid>
      </AccordionDetails>
    </Accordion>
  )
}

export default MappedJobAccordion
